import * as React from 'react'
import { useMemo } from 'react'

import { ENTITY_TYPES } from '@sales-center/common'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import type { IEntityFormSetting } from '@user/store/entityFormSettings/typings'

import { FieldSettingsCard } from './FieldSettingsCard/FieldSettingsCard'

type Props = {
  settings: IEntityFormSetting[]
  allSettings: IEntityFormSetting[]
  entityType: ENTITY_TYPES
}

/**
 * Renders one `FieldSettingsCard` per field setting.
 *
 * Reads the Tidal theme flag once and passes it down, so the cards stay
 * presentational and a single card component covers both theme states.
 *
 * Each card receives `settings` (this section, in display order) as
 * `sectionSettings` for combine-state adjacency, and the separate
 * `allSettings` (the whole entity, unfiltered by section) so required-group
 * validation can see sibling fields that live in a different section.
 *
 * @param settings The field settings to render, already ordered and filtered
 *   to this section.
 * @param allSettings The entity's full, unfiltered field-setting list.
 * @param entityType Which entity's field registry to resolve each field against.
 */
export function FieldSettingsCardList(props: Props) {
  const { settings, allSettings, entityType } = props
  const isTidalThemeActive = useTidalThemeActive()

  const cards = useMemo(
    () =>
      settings.map((setting, index) => (
        <FieldSettingsCard
          key={`setting_${index}`}
          settingItem={setting}
          sectionSettings={settings}
          allSettings={allSettings}
          entityType={entityType}
          isTidalThemeActive={isTidalThemeActive}
        />
      )),
    [settings, allSettings, entityType, isTidalThemeActive],
  )

  return <>{cards}</>
}
FieldSettingsCardList.displayName = 'FieldSettingsCardList'
